/**
 * locationStore — persistent "where are we delivering" picker that powers
 *   • the header location chip
 *   • the default delivery address on checkout
 *   • the radius bias for nearby stores / recommended products
 *
 * Persists to localStorage so picking your home neighbourhood once is enough.
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface PickedLocation {
  /** Free-text label shown in the header chip ("HSR Layout, Bengaluru"). */
  label: string;
  /** Optional full address (e.g. from Mapbox reverse geocoding). */
  address?: string;
  latitude: number;
  longitude: number;
  /** Tag for the address book ("Home", "Work", "Other"). */
  tag?: 'home' | 'work' | 'other';
  /** ISO timestamp this location was last picked. */
  pickedAt?: string;
}

interface LocationState {
  current: PickedLocation | null;
  recents: PickedLocation[];
  setCurrent: (loc: PickedLocation) => void;
  clear: () => void;
}

const MAX_RECENTS = 6;

export const useLocationStore = create<LocationState>()(
  persist(
    (set, get) => ({
      current: null,
      recents: [],
      setCurrent: (loc) => {
        const enriched: PickedLocation = { ...loc, pickedAt: new Date().toISOString() };
        const recents = [
          enriched,
          ...get().recents.filter((r) => r.label !== loc.label),
        ].slice(0, MAX_RECENTS);
        set({ current: enriched, recents });
      },
      clear: () => set({ current: null }),
    }),
    {
      name: 'pm-location',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ current: s.current, recents: s.recents }),
    },
  ),
);

export default useLocationStore;

